/**
 * 对客文案统一脱敏(OEM 贴牌 0929,C-C)—— 把上游厂商 / 模型名从「给用户看的字」里抹掉。
 *
 * ═══ 为什么要有 ═══════════════════════════════════════════════════════════════
 * 后端回的失败原因(errorMessage / failureReason / ApiError.message)常原样带着上游名:
 * 「DashScope 返回 400」「doubao-seedance-2-0-fast 超时」「TikHub 返回非 200」。
 * 直营时是泄漏供应链,贴牌后更是直接穿帮。全站对客出口统一过 {@link maskVendor}:
 *   - apiErrors.errorToast / errorText(全站失败提示唯一口径)内部已调用;
 *   - 直接把 DTO 上的 errorMessage / failureReason 画到页面的地方,逐处包一层。
 *
 * ═══ 超管非代客态不脱敏 ════════════════════════════════════════════════════════
 * 平台超管排障要看真名,由 AuthProvider 按「真实身份=PLATFORM_ADMIN 且不在代客」调用
 * {@link setVendorReveal}(true)。代客时看到的必须和租户一模一样,所以代客态照样脱敏。
 *
 * ═══ 实现口径 ═════════════════════════════════════════════════════════════════
 * - 拉丁词按「整段 token」替换:段首含厂商片段的连续 [A-Za-z0-9._-] 串整体换掉
 *   (`doubao-seedance-2-0-fast-260128`、`dashscope.aliyuncs.com` 一刀收),大小写不敏感。
 * - 中文词按字面替换。
 * - 替换后相邻重复的占位词合并(「豆包 Seedance」→「模型服务」而不是「模型服务 模型服务」)。
 * - ★不用后行断言(lookbehind):老 iOS 微信 WebView 解析到就整包 SyntaxError。
 */

/** 取片(抓短视频原片)供应商。 */
const FETCH_SERVICE = "取片服务";
/** 语音转写供应商。 */
const ASR_SERVICE = "语音转写服务";
/** 其余一律:模型 / 生成服务。 */
const MODEL_SERVICE = "模型服务";

/** 拉丁厂商片段(小写;token 里含其一即整段替换)→ 占位词。顺序 = 优先级。 */
const LATIN_VENDOR_FRAGMENTS: ReadonlyArray<readonly [string, string]> = [
  ["tikhub", FETCH_SERVICE],
  ["quanmiao", ASR_SERVICE],
  ["openai", MODEL_SERVICE],
  ["chatgpt", MODEL_SERVICE],
  ["gpt", MODEL_SERVICE],
  ["gemini", MODEL_SERVICE],
  ["google", MODEL_SERVICE],
  ["anthropic", MODEL_SERVICE],
  ["claude", MODEL_SERVICE],
  ["dashscope", MODEL_SERVICE],
  ["bailian", MODEL_SERVICE],
  ["aliyun", MODEL_SERVICE],
  ["tongyi", MODEL_SERVICE],
  ["qwen", MODEL_SERVICE],
  ["wanx", MODEL_SERVICE],
  ["wan2", MODEL_SERVICE],
  ["wanxiang", MODEL_SERVICE],
  ["cosyvoice", MODEL_SERVICE],
  ["zhipu", MODEL_SERVICE],
  ["bigmodel", MODEL_SERVICE],
  ["glm", MODEL_SERVICE],
  ["doubao", MODEL_SERVICE],
  ["seedance", MODEL_SERVICE],
  ["seedream", MODEL_SERVICE],
  ["seadream", MODEL_SERVICE],
  ["volcengine", MODEL_SERVICE],
  ["volces", MODEL_SERVICE],
  ["bytedance", MODEL_SERVICE],
  ["byteplus", MODEL_SERVICE],
  ["jimeng", MODEL_SERVICE],
  ["kling", MODEL_SERVICE],
  ["happyhorse", MODEL_SERVICE],
  ["deepseek", MODEL_SERVICE],
  ["moonshot", MODEL_SERVICE],
  ["kimi", MODEL_SERVICE],
  ["minimax", MODEL_SERVICE],
  ["hailuo", MODEL_SERVICE],
  ["vapeur", MODEL_SERVICE],
  ["meridian", MODEL_SERVICE],
  ["evolink", MODEL_SERVICE],
  ["openrouter", MODEL_SERVICE],
];

/** 中文厂商词(字面,长词在前)→ 占位词。 */
const CJK_VENDOR_WORDS: ReadonlyArray<readonly [string, string]> = [
  ["全妙", ASR_SERVICE],
  ["通义万相", MODEL_SERVICE],
  ["通义千问", MODEL_SERVICE],
  ["通义百炼", MODEL_SERVICE],
  ["阿里云百炼", MODEL_SERVICE],
  ["阿里云", MODEL_SERVICE],
  ["通义", MODEL_SERVICE],
  ["万相", MODEL_SERVICE],
  ["千问", MODEL_SERVICE],
  ["百炼", MODEL_SERVICE],
  ["智谱", MODEL_SERVICE],
  ["豆包", MODEL_SERVICE],
  ["火山引擎", MODEL_SERVICE],
  ["火山方舟", MODEL_SERVICE],
  ["即梦", MODEL_SERVICE],
  ["可灵", MODEL_SERVICE],
  ["海螺", MODEL_SERVICE],
  ["月之暗面", MODEL_SERVICE],
  ["深度求索", MODEL_SERVICE],
];

const TOKEN_RE = /[A-Za-z0-9][A-Za-z0-9._-]*/g;

let reveal = false;

/** 超管非代客态打开(不脱敏 + 档位名后附真名);其余一律关。由 AuthProvider 调。 */
export function setVendorReveal(on: boolean): void {
  reveal = on;
}

/** 当前是否对厂商真名放行(engineLabel 附真名也读它)。 */
export function isVendorRevealed(): boolean {
  return reveal;
}

/**
 * 片段只在「段首」命中:token 开头,或紧跟 - _ . / 之后,或 camelCase 的大写处
 * (`doubao-seedance-2-0`、`LLM_OPENAI_ERROR`、`callDoubaoApi` 都中;`sparkling` 不会因 kling 误伤)。
 */
const LATIN_VENDOR_RES: ReadonlyArray<readonly [RegExp, string]> = LATIN_VENDOR_FRAGMENTS.map(
  ([frag, repl]) => [new RegExp(`(^|[-_./])${frag}`), repl] as const
);

function latinReplacement(token: string): string | null {
  // 原样小写一遍(OpenAI / DashScope 这种厂商自己的驼峰)+ 驼峰拆段一遍(callDoubaoApi)。
  const lower = token.toLowerCase();
  const split = token.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
  for (const [re, repl] of LATIN_VENDOR_RES) {
    if (re.test(lower) || re.test(split)) return repl;
  }
  return null;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const PLACEHOLDERS = [FETCH_SERVICE, ASR_SERVICE, MODEL_SERVICE];
const COLLAPSE_RE = new RegExp(
  `(${PLACEHOLDERS.map(escapeRe).join("|")})(?:[\\s·/、,,-]*\\1)+`,
  "g"
);

/**
 * 纯函数版(不看 reveal 开关),单测与需要强制脱敏的场景用。
 */
export function maskVendorAlways(text: string): string {
  if (!text) return text;
  let out = text.replace(TOKEN_RE, (tok) => latinReplacement(tok) ?? tok);
  for (const [word, repl] of CJK_VENDOR_WORDS) {
    if (out.includes(word)) out = out.split(word).join(repl);
  }
  return out.replace(COLLAPSE_RE, "$1");
}

/** 文案里有没有残留厂商词(守门单测用)。 */
export function containsVendorWord(text: string): boolean {
  return maskVendorAlways(text) !== text;
}

/**
 * 对客文案脱敏:超管非代客态原样返回,其余把厂商 / 模型名换成中性词。
 * null / undefined 原样透传,方便 `maskVendor(task.errorMessage) || "兜底句"` 写法。
 */
export function maskVendor(text: string): string;
export function maskVendor(text: string | null | undefined): string | null | undefined;
export function maskVendor(text: string | null | undefined): string | null | undefined {
  if (text == null || text === "") return text;
  if (reveal) return text;
  return maskVendorAlways(text);
}
